import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteAllReadNotifications,
  deleteNotification,
  getNotifications,
  markAllNotificationsRead,
} from '@/services/api.client';
import type { ListNotificationsParams } from '@/services/api.client';

export function useNotifications(params?: ListNotificationsParams) {
  return useQuery({
    queryKey: ['notifications', params],
    queryFn: () => getNotifications(params),
  });
}

/**
 * Shared unread-notifications query.
 *
 * Fetches the most recent unread notifications (small page) on a 5-minute
 * poll. Both the header badge ({@link useUnreadNotificationCount}) and the
 * browser-notification bootstrap ({@link useWebNotifications}) read from this
 * single query, so there is exactly one network poll regardless of how many
 * consumers mount — React Query dedupes by query key.
 *
 * The interval is 5 minutes because the WAF rate-limits this endpoint when
 * polled at 60s intervals; the badge and OS alerts do not need sub-minute
 * freshness (time-sensitive walk-in approvals have their own page polling).
 */
export function useUnreadNotifications() {
  return useQuery({
    queryKey: ['notifications', { isRead: false, pageSize: 10 }],
    queryFn: () => getNotifications({ isRead: false, pageSize: 10 }),
    staleTime: 5 * 60_000,
    refetchInterval: 5 * 60_000,
  });
}

export function useUnreadNotificationCount() {
  const { data } = useUnreadNotifications();
  return data?.meta.total ?? 0;
}

export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useDeleteNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteNotification(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useDeleteAllReadNotifications() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteAllReadNotifications,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}
